import { scopedThreadKey, scopeThreadRef } from "@t3tools/client-runtime";
import { useUiStateStore } from "../uiStateStore";
import {
  resolveThreadStatusPill,
  THREAD_STATUS_PILL_LABELS,
  type ThreadStatusPill,
} from "./Sidebar.logic";
import type { SidebarThreadSummary } from "../types";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { useTranslation } from "react-i18next";

export function ThreadStatusLabel({ status }: { status: ThreadStatusPill }) {
  const { i18n } = useTranslation();
  const statusLabel = i18n.t(THREAD_STATUS_PILL_LABELS[status.label]);

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            aria-label={statusLabel}
            className={`inline-flex size-3.5 shrink-0 items-center justify-center ${status.colorClass}`}
          />
        }
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${status.dotClass} ${
            status.pulse ? "animate-pulse" : ""
          }`}
        />
      </TooltipTrigger>
      <TooltipPopup side="top">{statusLabel}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * Non-interactive leading status icons for a thread row in compact contexts
 * like the command palette. Shows the change request state icon (if present) and the
 * thread status dot.
 */
export function ThreadRowLeadingStatus({ thread }: { thread: SidebarThreadSummary }) {
  const threadRef = scopeThreadRef(thread.environmentId, thread.id);
  const lastVisitedAt = useUiStateStore(
    (state) => state.threadLastVisitedAtById[scopedThreadKey(threadRef)],
  );
  const threadStatus = resolveThreadStatusPill({
    thread: {
      ...thread,
      lastVisitedAt,
    },
  });

  if (!threadStatus) {
    return null;
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      <ThreadStatusLabel status={threadStatus} />
    </span>
  );
}
